import { CurrencyMapItem, Locale, PointOfSale, currencyMap } from '@flysas-tech/book-value-stream-models';

import { LocalCurrencyFormat } from '../constants';

/**
 * @param {number} value - The numeric value to format as currency.
 * @param {string} pos - The point of sale identifier (used to retrieve the currency configuration).
 * @param {boolean} [displayLocalFormat=false] - If true, uses local currency format (prefix/suffix of the currency symbol). This should be used for the frontend.
 * @param {boolean} [forceMinPrecision=false] - If true, forces minimum precision to 0 for whole numbers.
 *  @param {boolean} [enforcedLocale=false] - If true, forces the locale to be SV_SE for formatting.
 * @returns {string} The formatted currency string.
 */

export const formatCurrency = (
  value: number,
  pos: string,
  options: { displayLocalFormats?: boolean; forceMinPrecision?: boolean; enforcedLocale?: boolean } = {
    displayLocalFormats: false,
    forceMinPrecision: false,
    enforcedLocale: false,
  }
): string => {
  const { CURRENCY_CODE, PRECISION, locale } = getCurrencyByPos(pos);
  const hasDecimals = value % 1 !== 0;
  const forceCleanFormat = options.enforcedLocale ? Locale.SV_SE : locale;
  const formattedValue = new Intl.NumberFormat(forceCleanFormat, {
    minimumFractionDigits: options.forceMinPrecision && !hasDecimals ? 0 : PRECISION,
    maximumFractionDigits: PRECISION,
  }).format(value);

  if (options.displayLocalFormats) {
    const { symbol, position } = LocalCurrencyFormat[CURRENCY_CODE as keyof typeof LocalCurrencyFormat] || {
      symbol: CURRENCY_CODE,
      position: 'prefix',
    };

    return position === 'prefix' ? `${symbol}${formattedValue}` : `${formattedValue}${symbol}`;
  }

  return `${formattedValue}\u00a0${CURRENCY_CODE}`;
};

/**
 * @param {number} value - The numeric value to format as currency.
 * @param {string} pos - The point of sale identifier (used to retrieve the currency configuration).

 * @returns {string} The raw value with currency code (value is screen reader compatible).
 */

export const getRawValueWithCurrency = (value: number, pos: string): string => {
  const { CURRENCY_CODE } = getCurrencyByPos(pos);
  return `${value} ${CURRENCY_CODE}`;
};

export const getCurrencyByPos = (pos: string): CurrencyMapItem => {
  const config = currencyMap[pos as PointOfSale];
  if (!config) throw new Error(`Invalid POS: ${pos}`);
  return config;
};
